import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

function whatsappUrl(telefone) {
  const digits = (telefone || '').replace(/\D/g, '')
  const number = digits.startsWith('55') ? digits : '55' + digits
  return `https://wa.me/${number}`
}

function calcPreview(item, qty, custoCompra) {
  const estoqueAtual = parseFloat(item.estoque) || 0
  const custoAtual = parseFloat(item.custo) || 0
  const q = parseFloat(qty) || 0
  const c = parseFloat(String(custoCompra).replace(',', '.')) || 0
  if (q <= 0) return null
  const novoEstoque = estoqueAtual + q
  const novoCusto = novoEstoque > 0 ? (estoqueAtual * custoAtual + q * c) / novoEstoque : c
  return { novoEstoque, novoCusto }
}

export default function Entradas() {
  const [itens, setItens] = useState([])
  const [categorias, setCategorias] = useState([])
  const [fornecedores, setFornecedores] = useState([])
  const [historico, setHistorico] = useState([])
  const [loading, setLoading] = useState(true)
  const [fornecedor, setFornecedor] = useState('')
  const [itensEntrada, setItensEntrada] = useState([])
  const [busca, setBusca] = useState('')
  const [filtrocat, setFiltrocat] = useState('')
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState('nova')
  const [confirm, setConfirm] = useState(null)
  const [lastAddedId, setLastAddedId] = useState(null)
  const qtyRefs = useRef({})
  const { msg, visible, show } = useToast()

  useEffect(() => { fetchTudo() }, [])

  // aviso ao sair com itens pendentes
  useEffect(() => {
    if (itensEntrada.length === 0) return
    const handler = e => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [itensEntrada])

  // auto-focus no qty do último item adicionado
  useEffect(() => {
    if (lastAddedId && qtyRefs.current[lastAddedId]) {
      qtyRefs.current[lastAddedId].focus()
      qtyRefs.current[lastAddedId].select()
      setLastAddedId(null)
    }
  }, [lastAddedId, itensEntrada])

  async function fetchTudo() {
    setLoading(true)
    const [{ data: it }, { data: cats }, { data: forn }, { data: hist }] = await Promise.all([
      supabase.from('itens').select('*').order('categoria').order('nome'),
      supabase.from('categorias').select('*').order('nome'),
      supabase.from('fornecedores').select('*').order('nome'),
      supabase.from('movimentos')
        .select('*, itens(nome), fornecedores(nome, telefone)')
        .eq('tipo', 'entrada')
        .order('created_at', { ascending: false })
        .limit(100),
    ])
    setItens(it || [])
    setCategorias(cats || [])
    setFornecedores(forn || [])
    setHistorico(hist || [])
    setLoading(false)
  }

  function addItem(item) {
    if (itensEntrada.find(e => e.item.id === item.id)) return
    setItensEntrada(prev => [...prev, { item, qty: '', custo: item.custo || '' }])
    setLastAddedId(item.id)
  }

  function removeItem(id) {
    setItensEntrada(prev => prev.filter(e => e.item.id !== id))
  }

  function updateEntrada(id, field, value) {
    setItensEntrada(prev => prev.map(e => e.item.id === id ? { ...e, [field]: value } : e))
  }

  const totalEntrada = itensEntrada.reduce((sum, e) => {
    const qty = parseFloat(e.qty) || 0
    const custo = parseFloat(String(e.custo).replace(',', '.')) || 0
    return sum + qty * custo
  }, 0)

  async function salvar() {
    if (itensEntrada.length === 0) { show('Adicione pelo menos um item'); return }
    for (const e of itensEntrada) {
      if (!e.qty || parseFloat(e.qty) <= 0) { show(`Quantidade inválida: ${e.item.nome}`); return }
      if (e.custo === '' || parseFloat(String(e.custo).replace(',', '.')) <= 0) { show(`Custo inválido: ${e.item.nome}`); return }
    }
    setSaving(true)
    const entradaId = crypto.randomUUID()
    const alterados = []
    const movimentosCriados = []

    for (const e of itensEntrada) {
      const qty = parseFloat(e.qty)
      const custoCompra = parseFloat(String(e.custo).replace(',', '.'))
      const preview = calcPreview(e.item, qty, custoCompra)

      const estoqueAnterior = parseFloat(e.item.estoque) || 0
      const custoAnterior = parseFloat(e.item.custo) || 0
      const { data: movimento, error: movError } = await supabase.from('movimentos').insert([{
        entrada_id: entradaId,
        item_id: e.item.id,
        fornecedor_id: fornecedor || null,
        tipo: 'entrada',
        quantidade: qty,
        custo_unitario: custoCompra,
      }]).select('id').single()
      if (movError || !movimento) {
        await reverterEntrada({ alterados, movimentosCriados })
        show('Erro ao registrar entrada. Nada foi aplicado.')
        setSaving(false)
        return
      }
      movimentosCriados.push(movimento.id)

      const { error: itemError } = await supabase.from('itens').update({
        estoque: preview.novoEstoque,
        custo: parseFloat(preview.novoCusto.toFixed(4)),
      }).eq('id', e.item.id)
      if (itemError) {
        await reverterEntrada({ alterados, movimentosCriados })
        show('Erro ao atualizar estoque. Entrada revertida.')
        setSaving(false)
        return
      }
      alterados.push({ itemId: e.item.id, estoqueAnterior, custoAnterior })
    }

    show('✅ Entrada registrada!')
    setSaving(false)
    setItensEntrada([])
    setFornecedor('')
    fetchTudo()
    setView('historico')
  }

  async function reverterEntrada(entrada) {
    for (const id of (entrada.movimentosCriados || [])) {
      await supabase.from('movimentos').delete().eq('id', id)
    }
    for (const item of [...(entrada.alterados || [])].reverse()) {
      await supabase.from('itens').update({
        estoque: item.estoqueAnterior,
        custo: item.custoAnterior,
      }).eq('id', item.itemId)
    }
  }

  async function excluirEntrada(movimento) {
    const { data: itemAtual } = await supabase.from('itens').select('estoque, custo').eq('id', movimento.item_id).single()
    if (!itemAtual) { show('Item não encontrado'); return }

    const stockAtual = parseFloat(itemAtual.estoque) || 0
    const custoAtual = parseFloat(itemAtual.custo) || 0
    const qty = parseFloat(movimento.quantidade)
    const custoEntrada = parseFloat(movimento.custo_unitario)
    const novoEstoque = stockAtual - qty
    const novoCusto = novoEstoque !== 0
      ? (custoAtual * stockAtual - custoEntrada * qty) / novoEstoque
      : custoAtual

    const { error: itemError } = await supabase.from('itens').update({
      estoque: novoEstoque,
      custo: parseFloat(Math.max(0, novoCusto).toFixed(4)),
    }).eq('id', movimento.item_id)
    if (itemError) {
      show('Erro ao atualizar estoque. Entrada nao removida.')
      return
    }

    const { error: deleteError } = await supabase.from('movimentos').delete().eq('id', movimento.id)
    if (deleteError) {
      await supabase.from('itens').update({
        estoque: stockAtual,
        custo: custoAtual,
      }).eq('id', movimento.item_id)
      show('Erro ao remover movimento. Estoque revertido.')
      return
    }

    show('Entrada removida')
    fetchTudo()
  }

  // agrupar histórico por entrada_id
  const entradasAgrupadas = historico.reduce((acc, m) => {
    const key = m.entrada_id || m.id
    if (!acc[key]) acc[key] = { id: key, created_at: m.created_at, fornecedor: m.fornecedores, itens: [] }
    acc[key].itens.push(m)
    return acc
  }, {})
  const gruposOrdenados = Object.values(entradasAgrupadas)

  function formatData(ts) {
    const d = new Date(ts)
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const cats = [...new Set(itens.map(i => i.categoria))]
  const itensFiltrados = itens.filter(i =>
    i.nome.toLowerCase().includes(busca.toLowerCase()) &&
    (filtrocat === '' || i.categoria === filtrocat) &&
    i.movimenta_estoque !== false &&
    !itensEntrada.find(e => e.item.id === i.id)
  )
  const fornecedorSelecionado = fornecedores.find(f => f.id === fornecedor)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Entradas</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn btn-sm ${view === 'nova' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('nova')}>
            Nova {itensEntrada.length > 0 && `(${itensEntrada.length})`}
          </button>
          <button className={`btn btn-sm ${view === 'historico' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setView('historico')}>Histórico</button>
        </div>
      </div>

      {loading && <div className="spinner">Carregando...</div>}

      {/* NOVA ENTRADA */}
      {!loading && view === 'nova' && (
        <div>
          {/* Fornecedor */}
          <div className="field">
            <label>Fornecedor (opcional)</label>
            <select value={fornecedor} onChange={e => setFornecedor(e.target.value)}>
              <option value="">— sem fornecedor —</option>
              {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          {fornecedorSelecionado?.telefone && (
            <a href={whatsappUrl(fornecedorSelecionado.telefone)} target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost btn-sm" style={{ marginTop: -8, marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              💬 Chamar {fornecedorSelecionado.nome}
            </a>
          )}

          {/* Itens adicionados */}
          {itensEntrada.length > 0 && (
            <div className="cost-box" style={{ marginBottom: 16 }}>
              {itensEntrada.map(e => {
                const preview = calcPreview(e.item, e.qty, e.custo)
                return (
                  <div key={e.item.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{e.item.nome}</div>
                      <button className="btn btn-danger btn-sm" onClick={() => removeItem(e.item.id)}>×</button>
                    </div>
                    <div className="row">
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label>Quantidade</label>
                        <input
                          ref={el => qtyRefs.current[e.item.id] = el}
                          type="number" min="0.01" step="0.01" placeholder="0"
                          value={e.qty}
                          onChange={ev => updateEntrada(e.item.id, 'qty', ev.target.value)}
                        />
                      </div>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label>Custo unitário (R$)</label>
                        <input
                          type="text" inputMode="decimal" placeholder="0,00"
                          value={e.custo}
                          onChange={ev => updateEntrada(e.item.id, 'custo', ev.target.value)}
                        />
                      </div>
                    </div>
                    {preview && (
                      <div style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 12 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          estoque: <strong>{e.item.estoque ?? 0}</strong> → <strong style={{ color: 'var(--green)' }}>{preview.novoEstoque}</strong>
                        </span>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          custo: <strong>R${parseFloat(e.item.custo).toFixed(2)}</strong> → <strong style={{ color: 'var(--pink-dark)' }}>R${preview.novoCusto.toFixed(2)}</strong>
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
              <div className="cost-row total">
                <span>Total da entrada</span>
                <span>R${totalEntrada.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Busca */}
          <div className="section-label">Adicionar itens</div>
          <div className="row" style={{ marginBottom: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <input placeholder="Buscar..." value={busca} onChange={e => setBusca(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <select value={filtrocat} onChange={e => setFiltrocat(e.target.value)}>
                <option value="">Todas</option>
                {categorias.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
          </div>

          {itensFiltrados.length === 0 && busca && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, padding: 16 }}>Nenhum item encontrado</div>
          )}

          {cats.filter(cat => filtrocat === '' || cat === filtrocat).map(cat => {
            const itensDaCat = itensFiltrados.filter(i => i.categoria === cat)
            if (itensDaCat.length === 0) return null
            return (
              <div key={cat}>
                <div style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', margin: '10px 0 4px' }}>{cat}</div>
                <div className="card" style={{ padding: '4px 16px' }}>
                  {itensDaCat.map(item => (
                    <div key={item.id} className="list-item">
                      <div>
                        <div className="item-name">{item.nome}</div>
                        <div className="item-sub">estoque: {item.estoque ?? '—'} · R${parseFloat(item.custo).toFixed(2)}</div>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => addItem(item)}>+ Add</button>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          {itensEntrada.length > 0 && (
            <button className="btn btn-primary" style={{ marginTop: 16, width: '100%' }} onClick={salvar} disabled={saving}>
              {saving ? 'Salvando...' : `Registrar entrada (${itensEntrada.length} ${itensEntrada.length === 1 ? 'item' : 'itens'})`}
            </button>
          )}
        </div>
      )}

      {/* HISTÓRICO agrupado */}
      {!loading && view === 'historico' && (
        <div>
          {gruposOrdenados.length === 0 && (
            <div className="empty">
              <div className="empty-icon">📦</div>
              <p>Nenhuma entrada registrada ainda.</p>
            </div>
          )}
          {gruposOrdenados.map(grupo => {
            const totalGrupo = grupo.itens.reduce((sum, m) => sum + parseFloat(m.quantidade) * parseFloat(m.custo_unitario), 0)
            return (
              <div key={grupo.id} className="card" style={{ marginBottom: 12, padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      {grupo.fornecedor?.nome || 'Sem fornecedor'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {formatData(grupo.created_at)}
                      {grupo.fornecedor?.telefone && (
                        <a href={whatsappUrl(grupo.fornecedor.telefone)} target="_blank" rel="noopener noreferrer"
                          style={{ marginLeft: 8, textDecoration: 'none' }}>💬</a>
                      )}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--pink-dark)' }}>
                    R${totalGrupo.toFixed(2)}
                  </div>
                </div>
                {grupo.itens.map(m => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 14 }}>{m.itens?.nome}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>+{m.quantidade} · R${parseFloat(m.custo_unitario).toFixed(2)}/un</span>
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirm(m)}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <p style={{ fontSize: 15, marginBottom: 6, textAlign: 'center', fontWeight: 600 }}>Excluir entrada?</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 8 }}>
              {confirm.itens?.nome} · +{confirm.quantidade} un · R${parseFloat(confirm.custo_unitario).toFixed(2)}/un
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 20 }}>
              O estoque será revertido.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={() => { excluirEntrada(confirm); setConfirm(null) }}>Excluir</button>
            </div>
          </div>
        </div>
      )}

      <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>
    </div>
  )
}
